import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, Injectable } from '@angular/core';
import { CustomizationService } from './customization.service';

export type EduTheme = 'dark' | 'light';

const CLASS_BY_THEME: Record<EduTheme, string> = { dark: 'theme-dark', light: 'theme-light' };

/**
 * Colour scheme of the app. Dark is the default; the choice is kept with the
 * other dashboard customizations (per browser) and applied as `theme-dark` /
 * `theme-light` on `<html>`, which both the token overrides in `styles.css`
 * and PrimeNG's dark-mode selector key on.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly customization = inject(CustomizationService);

  private readonly stored = this.customization.getCustomization('theme');

  readonly theme = computed<EduTheme>(() => (this.stored()?.value === 'light' ? 'light' : 'dark'));
  readonly isLight = computed(() => this.theme() === 'light');

  constructor() {
    effect(() => {
      const theme = this.theme();
      const root = this.document.documentElement;
      for (const [name, className] of Object.entries(CLASS_BY_THEME)) {
        root.classList.toggle(className, name === theme);
      }
    });
  }

  set(theme: EduTheme): void {
    this.customization.updateCustomizationKey('theme', theme, {
      isConfiguredByUser: true,
      shouldOverrideIfConfiguredByUser: true,
    });
  }

  toggle(): void {
    this.set(this.isLight() ? 'dark' : 'light');
  }
}
